import React, { useEffect, useState, useRef, useCallback } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useSelector } from 'react-redux';
import { StatGridSkeleton } from '../../components/skeleton/skeleton';
import { DataTableSkeleton } from '../../data/columns';
import {
  DocumentChartBarIcon,
  ArrowDownTrayIcon,
  BanknotesIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline';
import WeekSlider from '../../components/week-slider/week-slider';
import NoTransactions from '../../assets/no-transactions.svg';
import orderApi from '../../services/orderApi';
import storeApi from '../../services/storeApi';
import financeApi from '../../services/financeApi';
import Notification from '../../components/notification/notification';
import { formatCurrency } from '../../utils/baseUtils';
import { useNavigate } from 'react-router-dom';
import { setStartAndEndDate } from '../../utils/setStartAndEndDate';
import {
  TWButton,
  TWSelect,
  TWDateRangePicker,
  TWDataTable,
  TWModal,
  PageHeader,
  StatCard,
  EmptyState,
} from '../../components/ui';
import { buildFinanceColumns } from './columns';
import './finance.css';

dayjs.extend(customParseFormat);
const utc = require('dayjs/plugin/utc');
const timezone = require('dayjs/plugin/timezone');

dayjs.extend(utc);
dayjs.extend(timezone);

const FinanceLegacy = () => {
  const navigate = useNavigate();

  const globalCompany = useSelector((state) => state.companyReducer.selected);
  const [dialogs, setDialogs] = useState({ csv: false });
  const [csvForm, setCsvForm] = useState({
    startdate: '',
    enddate: '',
    storeid: null,
  });
  const cancelButtonRef = useRef(null);
  const roles = useSelector((state) => state.authReducer.roles);
  const [balance, setBalance] = useState(0);
  const [storeList, setStoreList] = useState([]);
  const [search, setSearch] = useState({
    storeid: null,
    startdate: dayjs().startOf('week'),
    enddate: dayjs().endOf('week'),
  });
  const [loading, setLoading] = useState({
    listLoading: false,
    storeListLoading: true,
    thisWeekOrdersLoading: true,
    upsertLoading: false,
    deleteLoading: false,
    csvLoading: false,
  });

  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });

  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalRecords: null,
  });
  const [financeList, setFinanceList] = useState([]);
  const [thisWeekStats, setThisWeekStats] = useState([]);

  const getBalance = async () => {
    try {
      const list = await financeApi.getBalanceById(globalCompany.id);
      setBalance(list.data.data);
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    }
  };

  const getFinanceList = async () => {
    try {
      const query = !roles.includes(100)
        ? `?companyid=${globalCompany.id}&pageNumber=${pagination.pageNumber}&pageSize=${pagination.pageSize}`
        : `?pageNumber=${pagination.pageNumber}&pageSize=${pagination.pageSize}`;

      const list = await financeApi.list(query);
      if (list.data.data.length > 0) {
        setFinanceList(list.data.data);
        setPagination({
          pageNumber: list.data.pageNumber,
          pageSize: list.data.pageSize,
          totalRecords: list.data.totalRecords,
        });
      } else {
        setFinanceList([]);
      }
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      setLoading((prev) => ({ ...prev, listLoading: false }));
    }
  };

  const getFinanceTypeList = async () => {
    try {
      setLoading((prev) => ({ ...prev, listLoading: true }));
      await financeApi.getTypeList('?pageSize=1000');
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    }
  };

  const getStoreList = async () => {
    try {
      const query = globalCompany.id ? `companyid=${globalCompany.id}` : '';
      const list = await storeApi.list(query);
      setStoreList(list.data.data.length > 0 ? list.data.data : []);
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
      setStoreList([]);
    }
  };

  const onCloseUpsert = () => setDialogs((prev) => ({ ...prev, csv: false }));
  const onClickDownloadCSVModal = () =>
    setDialogs((prev) => ({ ...prev, csv: true }));

  const onClickDownloadCSV = async () => {
    try {
      setLoading((prev) => ({ ...prev, csvLoading: true }));
      const query = `?startdate=${csvForm.startdate}&enddate=${csvForm.enddate}&storeid=${csvForm.storeid}`;
      const csvUrl = await orderApi.exportCsv(query);
      window.open(csvUrl.data, '_blank');
      onCloseUpsert();
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      setLoading((prev) => ({ ...prev, csvLoading: false }));
    }
  };

  const onChangeCsvForm = (dateString) => {
    if (!dateString) {
      setCsvForm((prev) => ({ ...prev, startdate: '', enddate: '' }));
      return;
    }
    setCsvForm((prev) => ({
      ...prev,
      startdate: dateString[0],
      enddate: dateString[1],
    }));
  };

  const calculateThisWeekOrdersTotal = (orderList) => {
    const totalStats = [
      { value: 0, name: 'Total Order' },
      { value: 0, name: 'Total Tax' },
      { value: 0, name: 'Total Tip' },
      { value: 0, name: 'Total Sales' },
    ];
    return orderList.reduce((acc, order) => {
      const updatedTotals = [...acc];
      updatedTotals[0].value += 1;
      updatedTotals[1].value += order.tax;
      updatedTotals[2].value += order.tip;
      updatedTotals[3].value +=
        order.subtotal +
        order.tip +
        order.crvtax +
        order.totalextrafee +
        order.tax -
        order.itemfee -
        ((order.stripefee > order.cardfee ? order.stripefee : order.cardfee) +
          order.discount +
          order.thiswayfee +
          order.refund);
      return updatedTotals;
    }, totalStats);
  };

  const getWeekRangeOrders = useCallback(async () => {
    const isAdmin = roles.includes(100);
    if (!isAdmin && !globalCompany?.id) {
      setThisWeekStats([]);
      return;
    }
    try {
      setLoading((prev) => ({ ...prev, thisWeekOrdersLoading: true }));
      const query = {
        companyid: globalCompany.id,
        paymentstatus: 20,
        startdate: setStartAndEndDate(search.startdate, 'startdate', '0'),
        enddate: setStartAndEndDate(search.enddate, 'enddate', '0'),
        storeid: search.storeid,
        pageSize: 1000,
      };
      const list = await orderApi.list(query);
      setThisWeekStats(calculateThisWeekOrdersTotal(list.data.data || []));
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      setLoading((prev) => ({ ...prev, thisWeekOrdersLoading: false }));
    }
  }, [globalCompany.id, search, roles]);

  const handleWeekChange = (startOfWeek, endOfWeek) => {
    setSearch((prev) => ({
      ...prev,
      startdate: startOfWeek,
      enddate: endOfWeek,
    }));
  };

  useEffect(() => {
    getFinanceTypeList();
    if (globalCompany?.id) {
      getStoreList();
      getBalance();
      getFinanceList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalCompany.id, pagination.pageNumber, pagination.pageSize]);

  useEffect(() => {
    getWeekRangeOrders();
  }, [getWeekRangeOrders]);

  const statToneMap = {
    'Total Order': 'blue',
    'Total Tax': 'amber',
    'Total Tip': 'emerald',
    'Total Sales': 'violet',
  };
  const statIconMap = {
    'Total Order': DocumentChartBarIcon,
    'Total Tax': ScaleIcon,
    'Total Tip': BanknotesIcon,
    'Total Sales': BanknotesIcon,
  };

  return (
    <div className="finance-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-5xl space-y-8">
        <PageHeader
          title="Finance"
          description="View and keep track of all your company financial transactions."
        />

        {loading.listLoading ? (
          <DataTableSkeleton
            rows={8}
            headers={[
              { label: 'Type', w: 'w-32' },
              { label: 'Explanation', w: 'w-48' },
              { label: 'Create Date', w: 'w-24' },
              { label: 'Total', align: 'right', w: 'w-20' },
            ]}
          />
        ) : (
          <div className="space-y-6">
            <div className="sm:max-w-xs">
              <StatCard
                icon={BanknotesIcon}
                tone="emerald"
                label="Current Balance"
                value={formatCurrency(balance)}
              />
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div className="w-full sm:w-64">
                <TWSelect
                  label="Store"
                  placeholder="Stores"
                  onChange={(e) =>
                    setSearch((prev) => ({ ...prev, storeid: e || null }))
                  }
                  value={search.storeid === null ? '' : search.storeid}
                  options={[
                    { value: '', label: 'All Stores' },
                    ...storeList.map((item) => ({
                      value: item.id,
                      label: item.name,
                    })),
                  ]}
                />
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <WeekSlider onWeekChange={handleWeekChange} weekOfStartMonday />
                <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
                  <TWButton
                    block
                    icon={DocumentChartBarIcon}
                    label="Summary"
                    className="sm:w-auto"
                    onClick={() =>
                      navigate(
                        `${globalCompany.id}/orders?thisWeek=true&storeid=${
                          search.storeid || ''
                        }&startdate=${setStartAndEndDate(
                          search.startdate,
                          'startdate',
                          '0',
                        )}&enddate=${setStartAndEndDate(
                          search.enddate,
                          'enddate',
                          '0',
                        )}`,
                      )
                    }
                  />
                  <TWButton
                    block
                    icon={ArrowDownTrayIcon}
                    label="Download"
                    className="sm:w-auto"
                    onClick={onClickDownloadCSVModal}
                  />
                </div>
              </div>
            </div>

            {loading.thisWeekOrdersLoading ? (
              <StatGridSkeleton count={4} />
            ) : (
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                {thisWeekStats.map((stat) => (
                  <StatCard
                    key={stat.name}
                    icon={statIconMap[stat.name]}
                    tone={statToneMap[stat.name]}
                    label={stat.name}
                    value={
                      stat.name === 'Total Order'
                        ? Number(stat.value).toLocaleString('en-US')
                        : formatCurrency(stat.value)
                    }
                  />
                ))}
              </div>
            )}

            <p className="text-sm text-slate-500 dark:text-slate-400">
              We automatically process payouts every <b>Monday</b>, which may
              take up to <b>two business</b> days to arrive. Please note that
              there may be delays on public holidays.
            </p>

            <section className="reveal">
              <h2 className="mb-3 text-lg font-semibold text-slate-900 dark:text-slate-100">
                All Transactions
              </h2>
              {financeList.length > 0 ? (
                <div>
                  <TWDataTable
                    columns={buildFinanceColumns({
                      companyId: globalCompany.id,
                    })}
                    data={financeList}
                    rowKey="id"
                    pagination={
                      pagination.totalRecords > 0
                        ? {
                            current: pagination.pageNumber,
                            pageSize: pagination.pageSize,
                            total: pagination.totalRecords,
                            showTotal: (total, range) =>
                              `Showing ${range[0]} to ${range[1]} of ${total} transactions`,
                            onChange: (page, pageSize) =>
                              setPagination((prev) => ({
                                ...prev,
                                pageNumber: page,
                                pageSize,
                              })),
                          }
                        : undefined
                    }
                  />
                </div>
              ) : (
                <EmptyState
                  image={NoTransactions}
                  title="No transactions"
                  description="New transactions will appear here."
                />
              )}
            </section>
          </div>
        )}
      </div>

      <TWModal
        open={dialogs.csv}
        onClose={onCloseUpsert}
        title="Download CSV"
        description="Choose a store and date range to export."
        size="xl"
        initialFocus={cancelButtonRef}
        showCloseButton
        footer={
          <div className="flex justify-end gap-2">
            <TWButton
              ref={cancelButtonRef}
              label="Cancel"
              disabled={loading.csvLoading}
              onClick={onCloseUpsert}
            />
            <TWButton
              variant="primary"
              icon={ArrowDownTrayIcon}
              label="Download CSV"
              loading={loading.csvLoading}
              onClick={onClickDownloadCSV}
            />
          </div>
        }
      >
        <div className="space-y-4">
          <TWSelect
            label="Store"
            placeholder="Stores"
            value={csvForm.storeid}
            onChange={(e) => setCsvForm((prev) => ({ ...prev, storeid: e }))}
            options={storeList.map((item) => ({
              value: item.id,
              label: item.name,
            }))}
          />
          <TWDateRangePicker label="Date Range" onChange={onChangeCsvForm} />
        </div>
      </TWModal>

      <Notification
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />
    </div>
  );
};

export default FinanceLegacy;
